import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Alert from '@material-ui/lab/Alert';
import Loader from '../../../components/Loader.js';
import { ReviewAPI } from '../../../api';
import Button from '../../../components/Button.js';
import { TextInputField } from '../../../components/input';
import Typography from '@material-ui/core/Typography';

const useStyles = makeStyles({
  root: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    alignContent: 'flex-start',
    justifyContent: 'flex-start',
    padding: '0',
  },
  controls: {
    padding: '1rem 0',
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  button: {
    marginLeft: '1rem',
  },
});

const ReviewResponse = ({reviewUuid, providerUuid, back}) => {
  const classes = useStyles();

  const [busy, setBusy] = React.useState(false);
  const [alertOpen, setAlertOpen] = React.useState(false);
  const [alertState, setAlterState] = React.useState(null);
  const [alertCaption, setAlterCaption] = React.useState('');
  const [review, setReview] = React.useState(null);
  const [comment, setComment] = React.useState('');
  const [commentUuid, setCommentUuid] = React.useState(null);

  const getData = async () => {
    const { payload } = await ReviewAPI.getReview(reviewUuid, true);
    setReview(payload);
    if (payload.comments.length) {
      // Ideally, there should be one comment (response) per review but
      // due to legacy data there can be more in some cases, hence we take
      // take the first item from the list.
      setComment(payload.comments[0]['content']);
      setCommentUuid(payload.comments[0]['uuid']);
    }
  };

  const triggerAlert = (caption, variant) => {
    setAlterCaption(caption);
    setAlterState(variant);
    setAlertOpen(true);
  };

  const closeAlert = () => {
    setAlertOpen(false);
  };

  const handleChange = event => {
    setComment(event.target.value);
  };

  // At the moment users can only create new comments/responses, not edit them
  // !! All comments are active immediately
  // TODO: provide a selection toggle for comment status
  const save = async () => {
    if (comment === '') {
      triggerAlert('Nothing entered', 'info');
      return null;
    }
    setBusy(true);
    try {
      if (commentUuid === null) {
        await ReviewAPI.createComment(reviewUuid, providerUuid, {content: comment, 'status': 'active'});
        triggerAlert('Response saved', 'success');
      } else {
        await ReviewAPI.updateComment(commentUuid, providerUuid, {content: comment, 'status': 'active'});
        triggerAlert('Response updated', 'success');
      }
    } catch (err) {
      triggerAlert('Save failed. Please contact us if this persists', 'warning');
    }
    setBusy(false);
  };

  React.useEffect(() => {
    getData();
  }, []); // eslint-disable-line

  if (review === null) {
    return (<Loader />);
  }

  return (
    <div className={classes.root}>
      <Typography variant="subtitle2">{review.author_username} wrote:</Typography>
      <Typography variant="body2">{review.content}</Typography>
      <TextInputField
        label="Your response"
        value={comment}
        multiline
        rows={10}
        onChange={handleChange}
      />
      <div className={classes.controls}>
        {alertOpen ? (
          <Alert severity={alertState} onClose={closeAlert}>
            {alertCaption}
          </Alert>
        ) : null}
        <Button
          variant="outlined"
          color="primary"
          onClick={back}
          label="Back"
          className={classes.button}
          pointer="left"
        />
        <Button
          variant="contained"
          color="primary"
          onClick={save}
          label="Save"
          busy={busy}
          className={classes.button}
          pointer="none"
        />
      </div>
    </div>
  );
};

ReviewResponse.propTypes = {
  reviewUuid: PropTypes.string.isRequired,
  providerUuid: PropTypes.string.isRequired,
  back: PropTypes.func.isRequired,
};

export default ReviewResponse;
